import React, { useState, useEffect } from 'react';
import UploadPanel from './components/UploadPanel';
import ResultsPanel from './components/ResultsPanel';
import { checkHealth, analyzeEDF } from './api';
import './styles/App.css';

export default function App() {
  const [serverStatus, setServerStatus] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [progressText, setProgressText] = useState('');
  const [results, setResults] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    checkHealth()
      .then((data) => setServerStatus(data))
      .catch(() => setServerStatus({ status: 'offline' }));
  }, []);

  const handleAnalyze = async (file, tasks) => {
    setIsLoading(true);
    setError(null);
    setResults(null);
    setProgress(0);
    setProgressText('Uploading file...');

    try {
      setProgressText('Uploading file...');

      const data = await analyzeEDF(
        file,
        tasks,
        {}, // use default best models
        (pct) => {
          setProgress(Math.min(pct, 50));
          if (pct >= 100) setProgressText('Processing EEG data...');
        }
      );

      setProgress(100);
      setProgressText('Analysis complete!');
      setResults(data);
    } catch (err) {
      const msg = err.response?.data?.detail || err.message || 'Analysis failed';
      setError(msg);
      setProgressText('');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="app">
      {/* Header */}
      <div className="header">
        <h1>
          EEG Seizure Reader
          <span className="version-badge">V2</span>
        </h1>
        <p className="subtitle">
          Multi-task EEG Analysis &mdash; Seizure Detection &bull; Onset Localization &bull; IED Detection
        </p>
        {serverStatus && (
          <p style={{ marginTop: 8, fontSize: '0.8rem' }}>
            {serverStatus.status === 'ok' ? (
              <span style={{ color: 'var(--accent-green)' }}>
                ● Server online &middot; {serverStatus.models_loaded} models &middot; {serverStatus.device}
              </span>
            ) : (
              <span style={{ color: 'var(--accent-red)' }}>
                ● Server offline — please wait or check deployment
              </span>
            )}
          </p>
        )}
      </div>

      {/* Upload */}
      <UploadPanel onAnalyze={handleAnalyze} isLoading={isLoading} />

      {/* Progress */}
      {isLoading && (
        <div className="card">
          <div className="progress-container">
            <div className="progress-bar">
              <div className="progress-bar-fill" style={{ width: `${progress}%` }} />
            </div>
            <div className="progress-text loading">{progressText}</div>
          </div>
        </div>
      )}

      {/* Error */}
      {error && (
        <div className="card" style={{ borderColor: 'var(--accent-red)' }}>
          <div className="card-header">
            <span className="icon">⚠️</span>
            <h2 style={{ color: 'var(--accent-red)' }}>Error</h2>
          </div>
          <p style={{ color: 'var(--text-secondary)' }}>{error}</p>
        </div>
      )}

      {/* Results */}
      <ResultsPanel results={results} />

      {/* Footer */}
      <div style={{ textAlign: 'center', padding: '30px 0 20px', color: 'var(--text-muted)', fontSize: '0.8rem' }}>
        <p>EEG Seizure Reader V2 &mdash; Prasat Neurological Institute &times; Rangsit University</p>
        <p style={{ marginTop: 4 }}>
          Trained on CHB-MIT Scalp EEG Database &middot; 5 Model Architectures &middot; 3 Analysis Tasks
        </p>
      </div>
    </div>
  );
}
